import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { settingsApi } from '@/lib/api/arrex/settings';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Truck, Loader2, Plus, Trash2, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { formatDeliveryDays } from '@/lib/format';
import { useApp } from '@/contexts/AppContext';
import { Collapse } from '@/components/ui/collapse';
import { readItems } from '@/lib/api/normalize';

type Area = {
  id: string;
  name: string;
  price: number;
  days: number;
  sortOrder: number;
  isActive: boolean;
  _new?: boolean;
  _deleted?: boolean;
  _dirty?: boolean;
};

const tmpId = () => `tmp_${Math.random().toString(36).slice(2, 10)}`;

export default function AdminShipping() {
  const { t } = useTranslation();
  const { lang } = useApp();
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulk, setBulk] = useState('10');
  const [senderName, setSenderName] = useState('');
  const [senderAccount, setSenderAccount] = useState('');
  const [senderDirty, setSenderDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await settingsApi.adminShippingAreas();
      setAreas(readItems<Area>(res));
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const loadSender = async () => {
    try {
      const { settings } = await settingsApi.adminGet();
      setSenderName(settings?.sender_name || '');
      setSenderAccount(settings?.sender_account_number || '');
      setSenderDirty(false);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadSender(); }, []);

  const update = (id: string, patch: Partial<Area>) => {
    setAreas(a => a.map(x => x.id === id ? { ...x, ...patch, _dirty: true } : x));
  };

  const remove = (id: string) => {
    setAreas(a => a.flatMap(x => {
      if (x.id !== id) return [x];
      if (x._new) return [];
      return [{ ...x, _deleted: true }];
    }));
  };

  const move = (idx: number, dir: -1 | 1) => {
    setAreas(a => {
      const visible = a.filter(x => !x._deleted).sort((n1, n2) => n1.sortOrder - n2.sortOrder);
      const target = visible[idx];
      const swap = visible[idx + dir];
      if (!target || !swap) return a;
      return a.map(x => {
        if (x.id === target.id) return { ...x, sortOrder: swap.sortOrder, _dirty: true };
        if (x.id === swap.id) return { ...x, sortOrder: target.sortOrder, _dirty: true };
        return x;
      });
    });
  };

  const addNew = () => {
    const maxSort = areas.reduce((m, x) => Math.max(m, x.sortOrder || 0), 0);
    setAreas(a => [...a, {
      id: tmpId(),
      name: '',
      price: 0, 
      days: 3,
      sortOrder: maxSort + 1,
      isActive: true,
      _new: true,
      _dirty: true,
    }]);
  };

  const applyBulk = (sign: 1 | -1) => {
    const amount = Number(bulk) || 0;
    if (amount <= 0) return;
    setAreas(a => a.map(x => x._deleted ? x : {
      ...x,
      price: Math.max(0, Number(x.price) + sign * amount),
      _dirty: true,
    }));
    toast.message(`Preview: ${sign > 0 ? '+' : '-'}${amount} EGP applied. Click Save Changes to persist.`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const toDelete = areas.filter(x => x._deleted && !x._new).map(x => x.id);
      const toInsert = areas.filter(x => x._new && !x._deleted);
      const toUpdate = areas.filter(x => !x._new && !x._deleted && x._dirty);

      for (const a of toInsert) {
        if (!a.name.trim()) {
          throw new Error('All new areas need a name');
        }
        if (!a.days || a.days < 1) {
          throw new Error('Delivery days must be at least 1');
        }
      }

      for (const id of toDelete) {
        await settingsApi.removeShippingArea(id);
      }
      for (const a of toInsert) {
        await settingsApi.createShippingArea({
          name: a.name,
          price: Number(a.price),
          days: Number(a.days) || 3,
          sortOrder: a.sortOrder,
          isActive: a.isActive,
        });
      }
      for (const a of toUpdate) {
        await settingsApi.updateShippingArea(a.id, {
          name: a.name,
          price: Number(a.price),
          days: Number(a.days) || 3,
          sortOrder: a.sortOrder,
          isActive: a.isActive,
        });
      }

      if (senderDirty) {
        await settingsApi.update({
          sender_name: senderName.trim() || null,
          sender_account_number: senderAccount.trim() || null,
        });
        setSenderDirty(false);
      }

      toast.success('Shipping settings saved');
      await load();
    } catch (e: any) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const visible = areas.filter(a => !a._deleted).sort((a, b) => a.sortOrder - b.sortOrder);
  const hasChanges = areas.some(a => a._dirty || a._deleted) || senderDirty;

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Invoice sender information */}
      <Collapse
        variant="outer"
        defaultOpen
        title={t('admin.shippingPage.senderTitle')}
        contentClassName="space-y-4"
      >
        <p className="text-xs text-muted-foreground">{t('admin.shippingPage.senderNote')}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">{t('admin.shippingPage.senderName')}</Label>
            <Input
              value={senderName}
              onChange={e => { setSenderName(e.target.value); setSenderDirty(true); }}
              placeholder="e.g. Arrex Store"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t('admin.shippingPage.senderAccount')}</Label>
            <Input
              value={senderAccount}
              onChange={e => { setSenderAccount(e.target.value); setSenderDirty(true); }}
              placeholder="e.g. 0123456789"
            />
          </div>
        </div>
      </Collapse>

      {/* Shipping areas */}
      <Collapse
        variant="outer"
        defaultOpen
        title={<span className="flex items-center gap-2"><Truck className="w-4 h-4 text-primary" /> {t('admin.shippingPage.areasTitle')}</span>}
        right={
          <Button type="button" onClick={addNew} variant="outline" size="sm">
            <Plus className="w-4 h-4 me-1" /> {t('admin.shippingPage.addArea')}
          </Button>
        }
        contentClassName="space-y-4"
      >
        <p className="text-xs text-muted-foreground">{t('admin.shippingPage.areasNote')}</p>

        {loading ? (
          <div className="flex items-center justify-center p-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin me-2" /> {t('admin.common.loading')}
          </div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            {/* Desktop header */}
            <div className="hidden md:grid grid-cols-[1fr_120px_140px_120px] gap-3 px-4 py-3 bg-muted/40 text-xs uppercase tracking-wider font-medium text-muted-foreground">
              <div>{t('admin.shippingPage.servedArea')}</div>
              <div>{t('admin.shippingPage.priceEgp')}</div>
              <div>{t('admin.shippingPage.deliveryDays')}</div>
              <div className="text-end">{t('admin.common.actions')}</div>
            </div>

            <div className="divide-y divide-border">
              {visible.length === 0 && (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  {t('admin.shippingPage.empty')}
                </div>
              )}
              {visible.map((a, idx) => (
                <div
                  key={a.id}
                  className="md:grid md:grid-cols-[1fr_120px_140px_120px] gap-3 px-4 py-3 flex flex-col"
                >
                  <div>
                    <Label className="md:hidden text-xs mb-1 block">Served Area</Label>
                    <Input
                      value={a.name}
                      onChange={e => update(a.id, { name: e.target.value })}
                      placeholder="Served Area Name"
                    />
                  </div>
                  <div>
                    <Label className="md:hidden text-xs mb-1 block">Price (EGP)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={a.price}
                      onChange={e => update(a.id, { price: Number(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label className="md:hidden text-xs mb-1 block">Delivery (days)</Label>
                    <div className="space-y-1">
                      <Input
                        type="number"
                        min={1}
                        max={60}
                        step={1}
                        value={a.days ?? 3}
                        onChange={e => update(a.id, { days: Math.max(1, Number(e.target.value) || 3) })}
                        placeholder="e.g. 3"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        {formatDeliveryDays(a.days ?? 3, a.days ?? 3, lang)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <Button type="button" variant="ghost" size="icon" onClick={() => move(idx, -1)} disabled={idx === 0} title={t('admin.common.moveUp')}>
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => move(idx, 1)} disabled={idx === visible.length - 1} title={t('admin.common.moveDown')}>
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => remove(a.id)} title={t('admin.common.delete')} className="text-destructive hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bulk adjustment */}
        <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold">{t('admin.shippingPage.bulkTitle')}</h3>
            <p className="text-xs text-muted-foreground">{t('admin.shippingPage.bulkNote')}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button type="button" onClick={() => applyBulk(-1)} variant="outline" size="icon" title={t('admin.shippingPage.decrease')}>
              <Minus className="w-4 h-4" />
            </Button>
            <Input
              type="number"
              min={0}
              value={bulk}
              onChange={e => setBulk(e.target.value)}
              className="w-28"
            />
            <Button type="button" onClick={() => applyBulk(1)} variant="outline" size="icon" title={t('admin.shippingPage.increase')}>
              <Plus className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground">{t('admin.shippingPage.bulkSuffix')}</span>
          </div>
        </div>
      </Collapse>

      <div className="flex items-center justify-end gap-3 sticky bottom-4">
        {hasChanges && <span className="text-xs text-accent">{t('admin.shippingPage.unsavedShort')}</span>}
        <Button
          type="button"
          onClick={save}
          disabled={saving || !hasChanges}
          className="bg-primary text-primary-foreground glow-primary"
        >
          {saving && <Loader2 className="w-4 h-4 me-1 animate-spin" />} {t('admin.common.saveChanges')}
        </Button>
      </div>
    </div>
  );
}
